/**
 * A circle an estimator draws around their face on a photo. `x`/`y` are its centre and `size` its
 * diameter, all as percentages: `x` and `size` of the image width, `y` of the image height.
 * Stored on the photo as `presentationOptions.objectPositionX/Y` + `focalSize`.
 */
export interface FocalCircle {
  x: number
  y: number
  size: number
}

export interface FocalPresentationOptions {
  objectPositionX?: number
  objectPositionY?: number
  focalSize?: number
}

/** Position and size of the photo inside a crop frame, as percentages of the frame */
export interface FocalCrop {
  left: number
  top: number
  width: number
  height: number
}

export const MIN_FOCAL_SIZE = 10
export const DEFAULT_FOCAL_SIZE = 40
/** Default share of the crop frame's width the circled area fills */
export const FOCAL_FILL = 0.6

function round(value: number) {
  return Math.round(value * 10) / 10
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

function finiteOr(value: number | undefined | null, fallback: number) {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

/** Largest diameter (percent of width) that still fits a circle inside an image of this aspect (width / height) */
export function getMaxFocalSize(imageAspect: number): number {
  if (!(imageAspect > 0)) return 100
  return Math.min(100, 100 / imageAspect)
}

/**
 * Keeps the circle fully inside the image: limits the size, then pulls the centre in from the
 * edges by the radius. `imageAspect` is width / height.
 */
export function clampFocalCircle(circle: FocalCircle, imageAspect: number): FocalCircle {
  const aspect = imageAspect > 0 ? imageAspect : 1
  const size = clamp(finiteOr(circle.size, DEFAULT_FOCAL_SIZE), MIN_FOCAL_SIZE, getMaxFocalSize(aspect))
  const radiusX = size / 2
  // the circle's diameter as a percentage of the image height
  const radiusY = (size * aspect) / 2
  return {
    x: round(clamp(finiteOr(circle.x, 50), radiusX, 100 - radiusX)),
    y: round(clamp(finiteOr(circle.y, 50), radiusY, 100 - radiusY)),
    size: round(size)
  }
}

/** Reads a stored circle; `size` is undefined when only a centre was saved (no zoom) */
export function getFocalCircle(
  options?: FocalPresentationOptions | null
): Omit<FocalCircle, 'size'> & { size?: number } {
  return {
    x: finiteOr(options?.objectPositionX, 50),
    y: finiteOr(options?.objectPositionY, 50),
    size: typeof options?.focalSize === 'number' && options.focalSize > 0 ? options.focalSize : undefined
  }
}

/**
 * Where to place a photo inside a crop frame so it covers the frame and centres on the circle.
 * With a circle size the photo is zoomed so the circle fills `fill` of the frame width; with
 * no size it only covers the frame (like `object-fit: cover`). The photo never leaves a gap.
 * Returns null when the photo's dimensions are unknown.
 */
export function getFocalCrop({
  options,
  imageWidth,
  imageHeight,
  frameAspect,
  fill = FOCAL_FILL
}: {
  options?: FocalPresentationOptions | null
  imageWidth?: number | null
  imageHeight?: number | null
  /** frame width / height */
  frameAspect: number
  /** share of the frame width the circle fills; 1 shows exactly the circled area */
  fill?: number
}): FocalCrop | null {
  if (!imageWidth || !imageHeight || !(frameAspect > 0)) return null
  const imageAspect = imageWidth / imageHeight
  const stored = getFocalCircle(options)
  const circle = stored.size
    ? clampFocalCircle(stored as FocalCircle, imageAspect)
    : { ...stored, size: undefined as number | undefined }

  // width of the photo as a percentage of the frame width: at least enough to cover the frame
  const coverWidth = Math.max(100, (100 * imageAspect) / frameAspect)
  const zoomWidth = circle.size ? (100 * fill) / (circle.size / 100) : 0
  const width = Math.max(coverWidth, zoomWidth)
  const height = (width * frameAspect) / imageAspect

  return {
    left: round(clamp(50 - (circle.x / 100) * width, 100 - width, 0)),
    top: round(clamp(50 - (circle.y / 100) * height, 100 - height, 0)),
    width: round(width),
    height: round(height)
  }
}

/** A rectangle on a photo as percentages: `x`/`width` of the image width, `y`/`height` of its height */
export interface CropArea {
  x: number
  y: number
  width: number
  height: number
}

/** The circle that a square crop area encloses */
export function cropAreaToFocalCircle(area: CropArea, imageAspect: number): FocalCircle {
  return clampFocalCircle({ x: area.x + area.width / 2, y: area.y + area.height / 2, size: area.width }, imageAspect)
}

/**
 * The square crop area around a stored circle. With no stored size, the circle is as large as
 * the photo allows, which matches the unzoomed cover crop.
 */
export function focalCircleToCropArea(
  circle: Omit<FocalCircle, 'size'> & { size?: number },
  imageAspect: number
): CropArea {
  const aspect = imageAspect > 0 ? imageAspect : 1
  const clamped = clampFocalCircle({ ...circle, size: circle.size ?? getMaxFocalSize(aspect) }, aspect)
  const height = clamped.size * aspect
  return {
    x: round(clamped.x - clamped.size / 2),
    y: round(clamped.y - height / 2),
    width: clamped.size,
    height: round(height)
  }
}
